
import React, { useState } from 'react';
import { Hourglass, Package, Wallet } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { saveRechargeToSupabase } from '@/lib/supabase';
import PageLayout from '@/components/PageLayout';
import AdBanner from '@/components/AdBanner';
import logo from '/lovable-uploads/40a4ee59-7714-4091-a6f7-e5ba5819fe5f.png';
import { useTranslation } from 'react-i18next';
import { dialUssd, formatUssdTelUrl } from '@/utils/ussd-utils';

const Solde = () => {
  const { t } = useTranslation();
  const [rechargeCode, setRechargeCode] = useState('');
  const [selectedAmount, setSelectedAmount] = useState<string | null>(null);
  
  const handleSoldeCheck = () => {
    dialUssd('*168#');
  };

  const handleForfaitCheck = () => {
    dialUssd('*165#');
  };

  const handleRechargeCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, '');
    setRechargeCode(numericValue);
  };

  const handleRecharge = () => {
    if (!rechargeCode) {
      return;
    }
    
    if (!selectedAmount) {
      return;
    }

    const rechargeData = {
      amount: selectedAmount,
      date: new Date().toISOString(),
      code: rechargeCode
    };
    
    const savedRecharges = JSON.parse(localStorage.getItem('recharges') || '[]');
    savedRecharges.push(rechargeData);
    localStorage.setItem('recharges', JSON.stringify(savedRecharges));
    
    try {
      saveRechargeToSupabase({
        recipientNumber: '', // Not applicable for self-recharge
        cardNumbers: rechargeCode,
        date: new Date().toISOString()
      });
      console.log('Recharge saved to Supabase');
    } catch (error) {
      console.error('Failed to save recharge to Supabase:', error);
    }
    
    window.location.href = formatUssdTelUrl(`*166#${rechargeCode}#`);
  };

  return (
    <PageLayout 
      title={t('solde.checkBalance')} 
      logo={logo}
      showBackButton
      colorScheme="bg-gradient-to-r from-amber-500 to-orange-500"
    >
      {/* Ad Banner for solde page */}
      <AdBanner pageSlug="solde" />
      
      <div className="space-y-2">
        <div 
          className="menu-item flex items-center p-4 border-b cursor-pointer bg-white rounded-lg shadow-sm hover:shadow-md transition-all"
          onClick={handleSoldeCheck}
        >
          <div className="menu-icon-container mr-4 p-2 rounded-full bg-blue-100">
            <Hourglass className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium">{t('solde.checkBalance')}</h2>
          </div>
          <div className="arrow-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        
        <div 
          className="menu-item flex items-center p-4 border-b cursor-pointer bg-white rounded-lg shadow-sm hover:shadow-md transition-all"
          onClick={handleForfaitCheck}
        >
          <div className="menu-icon-container mr-4 p-2 rounded-full bg-red-100">
            <Package className="h-6 w-6 text-red-500" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium">{t('solde.checkForfait')}</h2>
          </div>
          <div className="arrow-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white p-6 rounded-xl shadow-sm">
        <h2 className="text-xl font-semibold mb-6">{t('solde.rechargeTitle')}</h2>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
              {t('solde.cardNumber')}
            </label>
            <Input
              id="cardNumber"
              value={rechargeCode}
              onChange={handleRechargeCodeChange}
              placeholder={t('solde.enterCardNumber')}
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('solde.rechargeAmount')}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {['500', '1000', '2000', '5000', '10 000'].map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant={selectedAmount === amount ? "default" : "outline"}
                  className={`h-12 text-base ${
                    selectedAmount === amount ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white" : "bg-background"
                  }`}
                  onClick={() => setSelectedAmount(amount)}
                >
                  {amount} DJF
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 mb-8">
        <div 
          className="menu-item flex items-center p-4 border rounded-md shadow-sm cursor-pointer bg-gradient-to-r from-amber-50 to-orange-50 hover:shadow-lg transition-all"
          onClick={handleRecharge}
        >
          <div className="menu-icon-container mr-4 p-2 rounded-full bg-orange-100">
            <Wallet className="h-6 w-6 text-orange-500" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium">{t('solde.creditRecharge')}</h2>
          </div>
          <div className="arrow-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Solde;
